import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { getPublicUrl } from './storageHelpers';
import { ArtType } from '../pages/art/Art';
import { ART_PER_PAGE } from '../pages/art/components/ArtGallery';

// Types from useArt.ts
export type Art = Database['public']['Tables']['art']['Row'] & {
  user_tag?: string;
  aiImage?: string;
  userPng?: string;
};

// Types from useArt3D.ts
export type Art3D = Database['public']['Tables']['art_3d']['Row'] & {
  aiImage?: string;
  userPng?: string;
  ai3dModel?: string;
};

// Combined art type for functions that return both 2D and 3D art
export type CombinedArt = (Art | Art3D) & {
  artType: ArtType;
};

export const useArtList = () => {
  /**
   * Lists all art (both 2D and 3D) with pagination
   * @param page - Page number for pagination
   * @param pageSize - Number of items per page
   * @returns Object containing combined arts array and hasMore flag
   */
  const listAllArt = useCallback(async (
    page: number,
    pageSize: number = ART_PER_PAGE,
  ): Promise<{ arts: CombinedArt[]; hasMore: boolean }> => {
    try {
      // Get both 2D and 3D art
      const [art2DResult, art3DResult] = await Promise.all([
        listAllArt2D(page, pageSize/2),
        listAllArt3D(page, pageSize/2),
      ]);

      // Combine the results and add artType field
      const combinedArts: CombinedArt[] = [
        ...art2DResult.arts.map(art => ({ ...art, artType: '2D' as const })),
        ...art3DResult.arts.map(art => ({ ...art, artType: '3D' as const })),
      ];

      // Sort by updated_at in descending order
      combinedArts.sort((a, b) => {
        const dateA = new Date(a.updated_at).getTime();
        const dateB = new Date(b.updated_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination to the combined results
      const start = 0; // We're already paginating in the individual queries
      const end = pageSize;
      const paginatedArts = combinedArts.slice(start, end);

      // Determine if there are more results
      const hasMore = art2DResult.hasMore || art3DResult.hasMore || combinedArts.length > end;

      const result = {
        arts: paginatedArts,
        hasMore,
      };

      // console.log('listAllArt:', result.arts.map(art => art.id));
      // console.log('listAllArt:', result);
      return result;
    } catch (error) {
      console.error('Error in listAllArt:', error);
      // Return whatever results we have, or empty array if none
      return { arts: [], hasMore: false };
    }
  }, []);

  /**
   * Lists user's art (both 2D and 3D) with pagination
   * @param userId - User ID to filter by, or null for all users
   * @param page - Page number for pagination
   * @param pageSize - Number of items per page
   * @param AIOnly - Whether to only include art with AI images
   * @returns Object containing combined arts array and hasMore flag
   */
  const listMyArt = useCallback(async (
    userId: string | null,
    page: number,
    pageSize: number = ART_PER_PAGE,
    AIOnly: boolean = false,
  ): Promise<{ arts: CombinedArt[]; hasMore: boolean }> => {
    try {
      // Get both 2D and 3D art
      const [art2DResult, art3DResult] = await Promise.all([
        listMyArt2D(userId, page, pageSize/2, AIOnly),
        listMyArt3D(userId, page, pageSize/2, AIOnly),
      ]);

      // Combine the results and add artType field
      const combinedArts: CombinedArt[] = [
        ...art2DResult.arts.map(art => ({ ...art, artType: '2D' as const })),
        ...art3DResult.arts.map(art => ({ ...art, artType: '3D' as const })),
      ];

      // Sort by updated_at in descending order
      combinedArts.sort((a, b) => {
        const dateA = new Date(a.updated_at).getTime();
        const dateB = new Date(b.updated_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination to the combined results
      const start = 0; // We're already paginating in the individual queries
      const end = pageSize;
      const paginatedArts = combinedArts.slice(start, end);

      // Determine if there are more results
      const hasMore = art2DResult.hasMore || art3DResult.hasMore || combinedArts.length > end;

      const result = {
        arts: paginatedArts,
        hasMore,
      };

      // console.log('listMyArt:', result);
      return result;
    } catch (error) {
      console.error('Error in listMyArt:', error);
      // Return whatever results we have, or empty array if none
      return { arts: [], hasMore: false };
    }
  }, []);

  /**
   * Lists all 2D art with pagination
   * @param page - Page number for pagination
   * @param pageSize - Number of items per page
   * @param userId - Optional user ID to filter by
   * @param excludeSelf - Optional flag to include/exclude art by the specified user
   * @returns Object containing arts array and hasMore flag
   */
  const listAllArt2D = useCallback(async (
    page: number,
    pageSize: number = ART_PER_PAGE,
    userId?: string,
    excludeSelf: boolean = false,
  ): Promise<{ arts: Art[]; hasMore: boolean }> => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    // Use a JOIN-like approach by querying the view or using filter logic
    // To be efficient, we fetch only the necessary page of art records that actually have history
    let artQuery = supabase
      .from('art')
      .select(`
        id, created_at, updated_at, art_name, user_id, deleted,
        art_history_entries!inner(ai_image, user_png)
      `, { count: 'exact' })
      .or('deleted.is.null,deleted.eq.false')
      .not('art_history_entries.ai_image', 'is', null)
      .not('art_history_entries.ai_image', 'eq', '')
      .order('updated_at', { ascending: false })
      .range(start, end);
    
    if (userId && excludeSelf === true) {
      artQuery = artQuery.neq('user_id', userId);
    }

    const { data, error, count } = await artQuery;

    if (error) throw error;
    if (!data || data.length === 0) {
      return { arts: [], hasMore: false };
    }

    const arts: Art[] = data.map(art => {
      // Because we used !inner, art_history_entries is an array of entries for that art
      // We grab the first one because the query handles the existence
      const historyEntry = Array.isArray(art.art_history_entries) 
        ? art.art_history_entries[0] 
        : art.art_history_entries;

      const artObj = {
        id: art.id,
        created_at: art.created_at,
        updated_at: art.updated_at,
        art_name: art.art_name || '',
        user_id: art.user_id || '',
        deleted: art.deleted,
        userPng: historyEntry?.user_png || null,
        aiImage: historyEntry?.ai_image || null,
      };

      if (artObj.userPng && !artObj.userPng.startsWith('data:')) {
        artObj.userPng = getPublicUrl(supabase, artObj.userPng);
      }
      if (artObj.aiImage && !artObj.aiImage.startsWith('data:')) {
        artObj.aiImage = getPublicUrl(supabase, artObj.aiImage);
      }
      return artObj;
    });

    const hasMore = (count || 0) > end + 1;
    const finalResponse = { arts, hasMore };

    // console.log('listAllArt2D:', finalResponse);
    return finalResponse;
  }, []);

  // const listAllArt2D = useCallback(async (
  //   page: number,
  //   pageSize: number = 8,
  // ): Promise<{ arts: Art[]; hasMore: boolean }> => {
  //   // Calculate pagination parameters
  //   const start = (page - 1) * pageSize;
  //   const end = start + pageSize - 1;

  //   // Query the art_with_latest_history view which already includes the latest history entry for each art
  //   let query = supabase
  //     .from('art_with_latest_history')
  //     .select(`
  //       *,
  //       profiles!art_user_id_fkey (user_tag)
  //     `, { count: 'exact' })
  //     .order('updated_at', { ascending: false })
  //     .range(start, end);

  //   // only include art with an AI image
  //   query = query.not('ai_image', 'is', null).not('ai_image', 'eq', '');

  //   const { data: artEntries, error, count } = await query;

  //   if (error) throw error;
    
  //   // Process the results
  //   const arts: Art[] = [];
    
  //   if (artEntries && artEntries.length > 0) {
  //     // Transform the view results into Art objects
  //     artEntries.forEach(entry => {
  //       // Get or create the art object
  //       let art = arts.find(a => a.id === entry.art_id);
        
  //       if (!art) {
  //         art = {
  //           id: entry.art_id,
  //           created_at: entry.created_at,
  //           updated_at: entry.updated_at,
  //           art_name: entry.art_name || '',
  //           user_id: entry.user_id || '',
  //           deleted: entry.deleted,
  //           // Format user_tag with @ prefix if available
  //           user_tag: entry.profiles?.user_tag ? '@' + entry.profiles.user_tag : '',
  //           aiImage: entry.ai_image,
  //           userPng: entry.user_png
  //         };
          
  //         // Clean up the profiles property
  //         delete entry.profiles;
          
  //         arts.push(art);
  //       }
  //     });
  //   }

  //   // Determine if there are more results
  //   const hasMore = (count || 0) > end + 1;

  //   const finalResponse = {
  //     arts,
  //     hasMore
  //   };

  //   // console.log('listAllArt2D:', finalResponse);
  //   return finalResponse;
  // }, []);

  const listMyArt2D = useCallback(async (
    userId: string | null,
    page: number,
    pageSize: number = ART_PER_PAGE,
    AIOnly: boolean = false,
  ): Promise<{ arts: Art[]; hasMore: boolean }> => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;
    
    let artQuery = supabase
      .from('art')
      .select(`
        *,
        art_history_entries(ai_image, user_png, updated_at)
      `, { count: 'exact' })
      .or('deleted.is.null,deleted.eq.false')
      .order('updated_at', { ascending: false })
      .range(start, end);
  
    if (userId) {
      artQuery = artQuery.eq('user_id', userId);
    }
    
    if (AIOnly) {
      // Cast breaks postgrest-js's deep type instantiation (TS2589) triggered by
      // .not() with an embedded-resource path on this long builder chain.
      artQuery = (artQuery as any).not('art_history_entries.ai_image', 'is', null);
    }
  
    const { data, error, count } = await artQuery;
    if (error) throw error;
  
    if (!data || data.length === 0) {
      return { arts: [], hasMore: false };
    }
  
    let arts: Art[] = data.map(entry => {
      // Sort local history entries to get the latest one
      const history = entry.art_history_entries || [];
      const latestHistory = history.sort((a: any, b: any) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0];
      
      const artObj = {
        id: entry.id,
        created_at: entry.created_at,
        updated_at: entry.updated_at,
        art_name: entry.art_name || '',
        user_id: entry.user_id || '',
        deleted: entry.deleted,
        aiImage: latestHistory?.ai_image || null,
        userPng: latestHistory?.user_png || null,
      };
      
      if (artObj.userPng && !artObj.userPng.startsWith('data:')) {
        artObj.userPng = getPublicUrl(supabase, artObj.userPng);
      }
      if (artObj.aiImage && !artObj.aiImage.startsWith('data:')) {
        artObj.aiImage = getPublicUrl(supabase, artObj.aiImage);
      }
      
      return artObj;
    });
    
    if (AIOnly) {
      arts = arts.filter(art => art.aiImage !== null);
    }
  
    const hasMore = (count || 0) > end + 1;
    const finalResponse = { arts, hasMore };
  
    // console.log('listMyArt2D:', finalResponse);
    return finalResponse;
  }, []);

  /**
   * Lists all 3D art with pagination
   * @param page - Page number for pagination
   * @param pageSize - Number of items per page
   * @returns Object containing arts array and hasMore flag
   */
  const listAllArt3D = useCallback(async (
    page: number,
    pageSize: number = ART_PER_PAGE,
  ): Promise<{ arts: Art3D[]; hasMore: boolean }> => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const { data, error, count } = await supabase
      .from('art_3d')
      .select(`
        id, created_at, updated_at, art_name, user_id, deleted,
        art_3d_history_entries!inner(ai_image, user_png)
      `, { count: 'exact' })
      .or('deleted.is.null,deleted.eq.false')
      .not('art_3d_history_entries.ai_image', 'is', null)
      .not('art_3d_history_entries.ai_image', 'eq', '')
      .order('updated_at', { ascending: false })
      .range(start, end);

    if (error) throw error;
    if (!data || data.length === 0) {
      return { arts: [], hasMore: false };
    }

    const arts: Art3D[] = data.map(art => {
      const historyEntry = Array.isArray(art.art_3d_history_entries) 
        ? art.art_3d_history_entries[0] 
        : art.art_3d_history_entries;
      
      const artObj = {
        id: art.id,
        created_at: art.created_at,
        updated_at: art.updated_at,
        art_name: art.art_name || '',
        user_id: art.user_id || '',
        deleted: art.deleted,
        userPng: historyEntry?.user_png || null,
        aiImage: historyEntry?.ai_image || null,
      };
      
      if (artObj.userPng && !artObj.userPng.startsWith('data:')) {
        artObj.userPng = getPublicUrl(supabase, artObj.userPng);
      }
      if (artObj.aiImage && !artObj.aiImage.startsWith('data:')) {
        artObj.aiImage = getPublicUrl(supabase, artObj.aiImage);
      }
      
      return artObj;
    });

    const hasMore = (count || 0) > end + 1;
    const finalResponse = { arts, hasMore };

    // console.log('listAllArt3D:', finalResponse);
    return finalResponse;
  }, []);

  const listMyArt3D = useCallback(async (
    userId: string | null,
    page: number,
    pageSize: number = 8,
    AIOnly: boolean = false,
  ): Promise<{ arts: Art3D[]; hasMore: boolean }> => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;
  
    let artQuery = supabase
      .from('art_3d')
      .select(`
        *,
        art_3d_history_entries(ai_image, user_png, updated_at)
      `, { count: 'exact' })
      .or('deleted.is.null,deleted.eq.false')
      .order('updated_at', { ascending: false })
      .range(start, end);
  
    if (userId) {
      artQuery = artQuery.eq('user_id', userId);
    }
  
    const { data, error, count } = await artQuery;
    if (error) throw error;
  
    if (!data || data.length === 0) {
      return { arts: [], hasMore: false };
    }
  
    let arts: Art3D[] = data.map(entry => {
      const history = entry.art_3d_history_entries || [];
      const latestHistory = history.sort((a: any, b: any) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0];
      
      const artObj = {
        id: entry.id,
        created_at: entry.created_at,
        updated_at: entry.updated_at,
        art_name: entry.art_name || '',
        user_id: entry.user_id || '',
        deleted: entry.deleted,
        aiImage: latestHistory?.ai_image || null,
        userPng: latestHistory?.user_png || null,
      };
      
      if (artObj.userPng && !artObj.userPng.startsWith('data:')) {
        artObj.userPng = getPublicUrl(supabase, artObj.userPng);
      }
      if (artObj.aiImage && !artObj.aiImage.startsWith('data:')) {
        artObj.aiImage = getPublicUrl(supabase, artObj.aiImage);
      }
      
      return artObj;
    });
    
    if (AIOnly) {
      arts = arts.filter(art => art.aiImage !== null);
    }
  
    const hasMore = (count || 0) > end + 1;
    const finalResponse = { arts, hasMore };
  
    // console.log('listMyArt3D:', finalResponse);
    return finalResponse;
  }, []);

  /**
   * Determines whether an art piece is 2D or 3D based on its ID
   * @param artId - The ID of the art to check
   * @returns Promise resolving to '2D', '3D', or null if not found
   */
  const getArtType = useCallback(async (artId: string): Promise<ArtType | null> => {
    try {
      // Check if the art exists in the 2D art table
      const { data: art2D, error: art2DError } = await supabase
        .from('art')
        .select('id')
        .eq('id', artId)
        .single();
      
      if (art2DError && art2DError.code !== 'PGRST116') {
        // PGRST116 is the error code for "no rows returned"
        console.error('Error checking 2D art:', art2DError);
        throw art2DError;
      }
      
      if (art2D) {
        return '2D';
      }
      
      // If not found in 2D art table, check the 3D art table
      const { data: art3D, error: art3DError } = await supabase
        .from('art_3d')
        .select('id')
        .eq('id', artId)
        .single();
      
      if (art3DError && art3DError.code !== 'PGRST116') {
        console.error('Error checking 3D art:', art3DError);
        throw art3DError;
      }
      
      if (art3D) {
        return '3D';
      }
      
      // If not found in either table, return null
      return null;
    } catch (error) {
      console.error('Error in getArtType:', error);
      return null;
    }
  }, []);

  return {
    listAllArt,
    listMyArt,
    listAllArt2D,
    listMyArt2D,
    listAllArt3D,
    listMyArt3D,
    getArtType,
  };
};